import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import AdminMediaLibrary from './AdminMediaLibrary';
import AdminLinkedInPreviewModal from './AdminLinkedInPreviewModal';

// The form now accepts an `initialData` prop for AI content
const AdminArticleForm = ({ articleId, initialData, onSave, onCancel, onBackToOptions }) => {
    const [formData, setFormData] = useState({
        title: '', author_name: '', category: '', tags: '',
        content: '', summary: '', banner_image_url: '', linkedin_published_at: null
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);
    const [isLinkedInModalOpen, setIsLinkedInModalOpen] = useState(false);
    const [linkedInPreviewText, setLinkedInPreviewText] = useState('');
    const [isPosting, setIsPosting] = useState(false);

    useEffect(() => {
        // If we are editing an existing article, fetch its data
        if (articleId) {
            setIsLoading(true);
            const fetchArticleData = async () => {
                try {
                    const token = localStorage.getItem('app_token');
                    const response = await axios.get(`/api/admin/articles/${articleId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setFormData({ ...response.data, tags: response.data.tags || '' });
                } catch (err) {
                    setError('Failed to load article data. Please try again.');
                } finally {
                    setIsLoading(false);
                }
            };
            fetchArticleData();
        } 
        // If we received initial data from the AI, populate the form with it
        else if (initialData) {
            // Support both old and new formats
            setFormData(prev => ({
                ...prev,
                title: initialData.title || '',
                summary: initialData.summary || '',
                content: initialData.content || initialData.articleContent || '',
                category: initialData.category || '',
                tags: initialData.tags || '',
            }));
        }
    }, [articleId, initialData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleContentChange = (event, editor) => {
        const data = editor.getData();
        setFormData(prev => ({ ...prev, content: data }));
    };

    const handleSummaryChange = (event, editor) => {
        const data = editor.getData();
        setFormData(prev => ({ ...prev, summary: data }));
    };

    const handleMediaSelect = (url) => {
        setFormData(prev => ({ ...prev, banner_image_url: url }));
        setIsMediaLibraryOpen(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        try {
            await onSave(formData);
        } catch (err) {
            setError(err.response?.data?.error || 'An unexpected error occurred. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenLinkedInPreview = async () => {
        const confirmationMessage = formData.linkedin_published_at
            ? "This article has been published to LinkedIn before. Are you sure you want to post it again?"
            : "You are about to post this article to LinkedIn. Continue?";

        if (window.confirm(confirmationMessage)) {
            try {
                const token = localStorage.getItem('app_token');
                const response = await axios.get(`/api/admin/articles/${articleId}/linkedin-preview`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setLinkedInPreviewText(response.data.linkedinPostText);
                setIsLinkedInModalOpen(true);
            } catch (err) {
                alert('Error: Could not generate a preview for the LinkedIn post.');
            }
        }
    };

    const handlePostToLinkedIn = async (finalText) => {
        setIsPosting(true);
        try {
            const token = localStorage.getItem('app_token');
            await axios.post(`/api/admin/articles/${articleId}/publish-linkedin`,
                { postText: finalText },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            alert('Article successfully published to LinkedIn!');
            if (articleId) {
                const response = await axios.get(`/api/admin/articles/${articleId}`, { headers: { Authorization: `Bearer ${token}` } });
                setFormData({ ...response.data, tags: response.data.tags || '' });
            }
        } catch (err) {
            alert('Error: Could not publish to LinkedIn.');
        } finally {
            setIsPosting(false);
            setIsLinkedInModalOpen(false);
        }
    };

    if (isLoading && articleId) return <p>Loading article details...</p>;

    return (
        <>
            {isLinkedInModalOpen && <AdminLinkedInPreviewModal initialText={linkedInPreviewText} onPost={handlePostToLinkedIn} onCancel={() => setIsLinkedInModalOpen(false)} isPosting={isPosting} />}
            {isMediaLibraryOpen && <AdminMediaLibrary s3Prefix="hj1/public/assets/images/" onSelect={handleMediaSelect} onCancel={() => setIsMediaLibraryOpen(false)} />}
            
            <div className="admin-modal-overlay">
                <div className="admin-modal-content">
                    <h2>{articleId ? 'Edit Article' : 'Add New Article'}</h2>
                    {error && <p className="error-message">{error}</p>}
                    <form onSubmit={handleSubmit} className="admin-form">
                        <div className="form-group">
                            <label htmlFor="title">Title</label>
                            <input type="text" name="title" value={formData.title} onChange={handleChange} required />
                        </div>
                        <div className="form-group">
                            <label htmlFor="author_name">Author</label>
                            <input type="text" name="author_name" value={formData.author_name} onChange={handleChange} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="category">Category</label>
                            <input type="text" name="category" value={formData.category} onChange={handleChange} required />
                        </div>
                        <div className="form-group">
                            <label htmlFor="tags">Tags (comma-separated)</label>
                            <input type="text" name="tags" value={formData.tags} onChange={handleChange} />
                        </div>
                        <div className="form-group">
                            <label>Banner Image</label>
                            <div className="media-input-group">
                                <input type="text" name="banner_image_url" value={formData.banner_image_url} readOnly placeholder="Select an image from the library..." />
                                <button type="button" className="btn-secondary" onClick={() => setIsMediaLibraryOpen(true)}>Select</button>
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Summary</label>
                            <CKEditor editor={ClassicEditor} data={formData.summary || ''} onChange={handleSummaryChange} />
                        </div>
                        <div className="form-group">
                            <label>Content</label>
                            <CKEditor editor={ClassicEditor} data={formData.content || ''} onChange={handleContentChange} />
                        </div>
                        <div className="form-actions">
                            <button type="button" onClick={onCancel} className="btn-secondary" disabled={isLoading || isPosting}>Cancel</button>
                            <button type="submit" className="btn-primary" disabled={isLoading || isPosting}>{isLoading ? 'Saving...' : 'Save Article'}</button>
                            {onBackToOptions && (
                                <button type="button" className="btn-secondary" style={{ marginLeft: 8 }} onClick={onBackToOptions} disabled={isLoading || isPosting}>
                                    Back to Options
                                </button>
                            )}
                            {articleId && ( // Only show publish button for existing articles
                                <button type="button" className="btn-primary" style={{ backgroundColor: '#0077b5' }} onClick={handleOpenLinkedInPreview} disabled={isPosting}>
                                    {isPosting ? 'Please wait...' : 'Publish to LinkedIn'}
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default AdminArticleForm;